import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Lightbulb, Home, Building2, Snowflake, Wrench, Trees } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    slug: "omni-lights",
    tag: "Featured",
    title: "Omni RGB Lights",
    desc: "Permanent, programmable architectural lighting with 16M colors and 120+ preset scenes. As a Certified Installer, we deliver factory-spec results.",
    Icon: Lightbulb,
    img: "https://images.unsplash.com/photo-1762786797257-95ab0b4624f2?auto=format&fit=crop&w=1400&q=80",
    span: "md:col-span-2 md:row-span-2",
    accent: true,
  },
  {
    slug: "permanent-home",
    tag: "Residential",
    title: "Permanent Home Lighting",
    desc: "Discreet channel-mounted fixtures that trace your roofline year-round. Zero ladders, zero storage.",
    Icon: Home,
    img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "permanent-business",
    tag: "Commercial",
    title: "Business Installations",
    desc: "Signage, façades, patios and parking lots. Programmable to your brand palette, always on-time.",
    Icon: Building2,
    img: "https://images.unsplash.com/photo-1764101498799-802446f744d8?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "holiday",
    tag: "Seasonal",
    title: "Holiday Lighting",
    desc: "Christmas, Fourth of July, game days — every celebration is a preset. Never climb a ladder again.",
    Icon: Snowflake,
  },
  {
    slug: "yard",
    tag: "Landscape",
    title: "Yard & Landscape",
    desc: "Path lights, uplighting, tree wraps, water features. Warm ambient beauty from dusk to dawn.",
    Icon: Trees,
    img: "https://images.unsplash.com/photo-1658692051708-519fbdac7e8f?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "maintenance",
    tag: "Service",
    title: "Omni Maintenance Contracts",
    desc: "Firmware updates, seasonal tune-ups, priority repairs. Keep your Omni system flawless year-round.",
    Icon: Wrench,
  },
];

export default function Services({ onQuote }) {
  return (
    <section id="services" className="section-pad relative">
      <div className="container-x">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-20 mb-14">
          <div className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-[color:var(--primary)]">
            [ Services ]
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display uppercase text-white text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[0.95] tracking-tight max-w-3xl">
              A full lighting<br />studio, on call.
            </h2>
            <p className="text-white/60 max-w-sm">
              From Omni RGB to landscape and holiday systems — one certified team, one warranty, one point of contact.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[minmax(220px,auto)] gap-4">
          {services.map((s, i) => (
            <motion.article
              key={s.slug}
              data-testid={SERVICES.card(s.slug)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden border border-white/10 bg-[#0C1017] p-6 md:p-8 flex flex-col justify-between min-h-[260px] hover:border-[color:var(--primary)]/50 transition-colors ${s.span || ""}`}
            >
              {s.img && (
                <div className="absolute inset-0 opacity-25 group-hover:opacity-40 transition-opacity duration-700">
                  <img src={s.img} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1017] via-[#0C1017]/70 to-transparent" />
                </div>
              )}

              <div className="relative z-10 flex items-start justify-between">
                <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[color:var(--primary)]">
                  {String(i + 1).padStart(2, "0")} · {s.tag}
                </span>
                <s.Icon size={s.accent ? 32 : 22} className="text-white/70 group-hover:text-[color:var(--primary)] transition-colors" />
              </div>

              <div className="relative z-10 mt-8">
                <h3 className={`font-display uppercase text-white leading-none tracking-tight ${s.accent ? "text-5xl md:text-6xl" : "text-2xl md:text-3xl"}`}>
                  {s.title}
                </h3>
                <p className="mt-4 text-white/60 text-sm md:text-base max-w-md leading-relaxed">
                  {s.desc}
                </p>
                <button
                  onClick={onQuote}
                  className="mt-6 inline-flex items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white group-hover:text-[color:var(--primary)] transition-colors"
                >
                  Get a Quote <ArrowUpRight size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>

              {s.accent && (
                <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-[color:var(--primary)]/20 blur-3xl pointer-events-none" />
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
